import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { MobileActionBar } from "@/components/layout/MobileActionBar";
import { QuoteProvider } from "@/components/QuoteModal";
import { SmoothScroll } from "@/components/SmoothScroll";
import { SiteImagesProvider } from "@/context/SiteImages";
import { INDEX_CONFIGS, PAGE_CONFIGS } from "@/data/pages";
import { initAnalytics } from "@/lib/analytics";
import Home from "@/pages/Home";
import IndexPage from "@/pages/IndexPage";
import DetailPage from "@/pages/DetailPage";
import Amc from "@/pages/Amc";
import Solar from "@/pages/Solar";
import Projects from "@/pages/Projects";
import About from "@/pages/About";
import Reviews from "@/pages/Reviews";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import TechAdvice from "@/pages/TechAdvice";
import TechAdviceCategory from "@/pages/TechAdviceCategory";
import TechAdviceArticle from "@/pages/TechAdviceArticle";
import Podcasts from "@/pages/Podcasts";
import PodcastEpisode from "@/pages/PodcastEpisode";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

function App() {
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <BrowserRouter>
      <SiteImagesProvider>
        <QuoteProvider>
          <SmoothScroll>
            <ScrollToTop />
            <a
              href="#main-content"
              data-testid="skip-to-content-link"
              className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:rounded-md focus:bg-cts-red focus:px-4 focus:py-2 focus:font-heading focus:font-bold focus:text-sm focus:text-white"
            >
              Skip to main content
            </a>
            <div className="flex min-h-screen flex-col bg-white">
              <Header />
              <main id="main-content" className="flex-1 pt-20 pb-16 lg:pb-0">
                <Routes>
                  <Route path="/" element={<Home />} />
                  {Object.values(INDEX_CONFIGS).map((cfg) => (
                    <Route key={cfg.path} path={cfg.path} element={<IndexPage config={cfg} />} />
                  ))}
                  {Object.values(PAGE_CONFIGS).map((cfg) => (
                    <Route key={cfg.path} path={cfg.path} element={<DetailPage config={cfg} />} />
                  ))}
                  <Route path="/amc" element={<Amc />} />
                  <Route path="/services/solar" element={<Solar />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/technology-advice" element={<TechAdvice />} />
                  <Route path="/technology-advice/category/:categorySlug" element={<TechAdviceCategory />} />
                  <Route path="/technology-advice/:slug" element={<TechAdviceArticle />} />
                  <Route path="/podcasts" element={<Podcasts />} />
                  <Route path="/podcasts/:slug" element={<PodcastEpisode />} />
                  <Route path="/podcasts/:slug/:langWord" element={<PodcastEpisode />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/reviews" element={<Reviews />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/privacy" element={<Legal kind="privacy" />} />
                  <Route path="/terms" element={<Legal kind="terms" />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
              <Footer />
              <MobileActionBar />
            </div>
          </SmoothScroll>
        </QuoteProvider>
      </SiteImagesProvider>
    </BrowserRouter>
  );
}

export default App;
